/**
 * 연결 정보를 제공하는 Provider입니다.
 *
 * 제공 데이터:
 * - electronAppInfo: Electron 앱 정보 (botId, apiPort 등)
 * - hostPort: API 서버 호스트:포트
 * - apiUrl: tRPC API URL
 * - token: cloud 모드에서 Authorization 헤더용 토큰
 * - isCloud: cloud 모드 여부
 * - engineUrl: cloud 모드에서 engine 접속 URL
 *
 * @remarks
 * Electron 환경에서는 electronAPI.getAppInfo()를 통해 Main 프로세스에서
 * 앱 정보를 가져옵니다. Cloud 환경에서는 sessionStorage에서 engineUrl/token을 읽습니다.
 * 웹 브라우저(비-Electron, 비-cloud) 환경에서는 electronAppInfo가 undefined입니다.
 */
import type { AppInfo } from '@bit-box/common';
import { createContextProvider } from '../common/create-context';
import React, { useEffect, useState } from 'react';
import { isElectron } from '../common/electron';
import { getHostPort, getApiUrl } from '../common/utils';

const [useConnectionContext, Provider] = createContextProvider<{
  electronAppInfo: AppInfo | undefined;
  hostPort: string;
  apiUrl: string;
  token?: string;
  isCloud: boolean;
  engineUrl?: string;
}>();

/**
 * 연결 정보를 제공하는 Provider.
 * Electron 환경에서는 Main 프로세스에서 botId, apiPort 등의 앱 정보를 가져옵니다.
 * Cloud 환경에서는 sessionStorage에서 engineUrl, token을 읽어 연결 정보를 구성합니다.
 * 웹 브라우저(비-Electron, 비-cloud) 환경에서는 electronAppInfo가 undefined입니다.
 */
const ConnectionProvider = ({ children }: { children: React.ReactNode }) => {
  const [electronAppInfo, setElectronAppInfo] = useState<AppInfo | undefined>(undefined);

  useEffect(() => {
    // Electron이 아닌 경우에는 electronAppInfo는 undefined.
    if (!isElectron) {
      return;
    }
    window.electronAPI.getAppInfo().then(info => {
      setElectronAppInfo(info);
    });
  }, []);

  // Electron인데 아직 appInfo를 가져오지 못한 경우 FrontEnd UI 표시를 늦춘다.
  if (isElectron && !electronAppInfo) {
    return null;
  }

  // cloud 모드 판정: sessionStorage에 engineUrl이 존재하면 cloud
  const engineUrl = typeof window !== 'undefined' ? sessionStorage.getItem('engineUrl') || undefined : undefined;
  const token = typeof window !== 'undefined' ? sessionStorage.getItem('token') || undefined : undefined;
  const isCloud = !!engineUrl;

  // cloud 모드: engineUrl에서 hostPort/apiUrl 추출
  let hostPort: string;
  let apiUrl: string;
  if (isCloud && engineUrl) {
    hostPort = new URL(engineUrl).host;
    apiUrl = `${engineUrl}/trpc`;
  } else {
    hostPort = getHostPort(electronAppInfo);
    apiUrl = getApiUrl(electronAppInfo);
  }

  return <Provider value={{ electronAppInfo, hostPort, apiUrl, token, isCloud, engineUrl }}>{children}</Provider>;
};

export { useConnectionContext, ConnectionProvider };
